import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UzInput } from '@utazzo/ui';
import { GripVertical, Search, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

export interface OrderedPickerOption {
  id: string;
  label: string;
  description?: string;
}

export interface OrderedPickerListProps {
  options: OrderedPickerOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
  maxSelected?: number;
  pickerLabel: string;
  pickerHint?: string;
  searchPlaceholder?: string;
  sortLabel?: string;
  sortEmptyMessage?: string;
  renderSortItemExtra?: (id: string, index: number) => ReactNode;
  /** Fallback label when the id is not in `options` (avoids showing raw UUIDs). */
  resolveLabel?: (id: string) => string | undefined;
}

export const OrderedPickerList = ({
  options,
  value,
  onChange,
  loading,
  disabled,
  maxSelected,
  pickerLabel,
  pickerHint,
  searchPlaceholder = 'Search…',
  sortLabel = 'Selected order',
  sortEmptyMessage = 'Nothing selected yet. Use the checklist above.',
  renderSortItemExtra,
  resolveLabel,
}: OrderedPickerListProps): React.JSX.Element => {
  const [search, setSearch] = useState('');

  const optionById = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const selectedSet = useMemo(() => new Set(value), [value]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(term) ||
        o.description?.toLowerCase().includes(term) ||
        o.id.toLowerCase().includes(term),
    );
  }, [options, search]);

  const atMax = maxSelected !== undefined && value.length >= maxSelected;

  const labelForId = (id: string): string =>
    optionById.get(id)?.label ?? resolveLabel?.(id) ?? 'Unknown item';

  const toggle = (id: string): void => {
    if (disabled) return;
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
      return;
    }
    if (atMax) return;
    onChange([...value, id]);
  };

  const toggleAllVisible = (): void => {
    if (disabled) return;
    const visibleIds = filteredOptions.map((o) => o.id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
    if (allVisibleSelected) {
      const visible = new Set(visibleIds);
      onChange(value.filter((id) => !visible.has(id)));
      return;
    }
    const next = [...value];
    for (const id of visibleIds) {
      if (selectedSet.has(id)) continue;
      if (maxSelected !== undefined && next.length >= maxSelected) break;
      next.push(id);
    }
    onChange(next);
  };

  const remove = (id: string): void => {
    if (disabled) return;
    onChange(value.filter((v) => v !== id));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const fromIdx = value.indexOf(String(active.id));
    const toIdx = value.indexOf(String(over.id));
    if (fromIdx < 0 || toIdx < 0) return;
    onChange(arrayMove(value, fromIdx, toIdx));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="space-y-1">
          <label className="text-sm font-medium text-zinc-700">{pickerLabel}</label>
          {pickerHint ? <p className="text-xs text-zinc-500">{pickerHint}</p> : null}
        </div>
        <div className="relative">
          <Search className="absolute top-2.5 left-2 h-3.5 w-3.5 text-zinc-400" />
          <UzInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 pl-8 text-sm"
            disabled={disabled || loading}
          />
        </div>
        <div className="max-h-52 overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50 p-2">
          {loading ? (
            <p className="py-4 text-center text-xs text-zinc-500">Loading…</p>
          ) : filteredOptions.length === 0 ? (
            <p className="py-4 text-center text-xs text-zinc-500">No options found.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {filteredOptions.map((row) => {
                const checked = selectedSet.has(row.id);
                const blocked = !checked && atMax;
                return (
                  <li key={row.id}>
                    <label
                      className={
                        'flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-white ' +
                        (blocked ? 'cursor-not-allowed opacity-50' : '')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled || blocked}
                        onChange={() => toggle(row.id)}
                      />
                      <span className="min-w-0 truncate">
                        {row.label}
                        {row.description ? (
                          <span className="ml-1 text-xs text-zinc-400">{row.description}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {filteredOptions.length > 0 && (
            <div className="mt-2 flex items-center justify-between border-t border-zinc-200 pt-2">
              <label className="flex items-center gap-2 text-xs text-zinc-500">
                <input
                  type="checkbox"
                  checked={
                    filteredOptions.length > 0 &&
                    filteredOptions.every((r) => selectedSet.has(r.id))
                  }
                  disabled={
                    disabled || (atMax && filteredOptions.some((r) => !selectedSet.has(r.id)))
                  }
                  onChange={toggleAllVisible}
                />
                Select all visible
              </label>
              <span className="text-xs text-zinc-500">
                {value.length}
                {maxSelected !== undefined ? ` / ${maxSelected}` : ''} selected
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-700">{sortLabel}</span>
          <span className="text-xs text-zinc-500">Drag to reorder</span>
        </div>
        {value.length === 0 ? (
          <p className="rounded-md border border-dashed border-zinc-200 px-3 py-4 text-center text-xs text-zinc-400">
            {sortEmptyMessage}
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={value} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {value.map((id, index) => (
                  <SortableSelectedRow
                    key={id}
                    id={id}
                    index={index}
                    label={labelForId(id)}
                    description={optionById.get(id)?.description}
                    disabled={disabled}
                    onRemove={() => remove(id)}
                    extra={renderSortItemExtra?.(id, index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

const SortableSelectedRow = ({
  id,
  index,
  label,
  description,
  disabled,
  onRemove,
  extra,
}: {
  id: string;
  index: number;
  label: string;
  description?: string;
  disabled?: boolean;
  onRemove: () => void;
  extra?: ReactNode;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={
        'rounded-md border border-zinc-200 bg-zinc-50 p-3 ' +
        (isDragging ? 'z-10 opacity-60 shadow-md' : '')
      }
    >
      <div className="flex items-start gap-2">
        {!disabled && (
          <button
            type="button"
            className="mt-0.5 cursor-grab rounded-md px-1 text-zinc-300 hover:bg-white hover:text-zinc-600 active:cursor-grabbing"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-medium text-zinc-500">#{index + 1}</p>
              <p className="truncate text-sm font-medium text-zinc-800">{label}</p>
              {description ? <p className="truncate text-xs text-zinc-400">{description}</p> : null}
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={onRemove}
                className="shrink-0 text-zinc-400 hover:text-rose-600"
                aria-label="Remove"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {extra}
        </div>
      </div>
    </div>
  );
};
